/**
 * The `{VAL}` **value slot** — upstream data syntax, rendered as an intentional inline token.
 *
 * `@massif/lancer-data`'s tag records carry their numeric value as a `{VAL}` placeholder in both
 * the tag's `name` and its `description` ("Threat {VAL}", "…Overwatch attacks within {VAL} spaces").
 * A tag *instance* on a weapon binds it — `tagLabel` in {@link ./compendiumData} substitutes the
 * real number, so an equipped weapon's TAGS row already reads "Threat 3". A tag *definition*, which
 * is what the GLOSSARY browses, has no value to bind: there the placeholder has nowhere to go and
 * was leaking to the screen as the literal string `{VAL}`.
 *
 * So this is the **unbound** half of that seam, not a second substitution path: where a real value
 * exists, `tagLabel` still wins; where there genuinely isn't one, the placeholder is **kept verbatim**
 * and marked up as a lightly-tinted mono token (`.compendium-val-slot`, compendium.css) that reads as
 * a deliberate slot rather than a missing value.
 *
 * Two renderers because the placeholder arrives in two shapes:
 *   - {@link ValText} for React text (list row titles, search hits, the detail heading),
 *   - {@link markValSlots} for compendium description HTML, which is injected with
 *     `dangerouslySetInnerHTML`.
 * Both are driven by {@link splitValSlots}, so the two can't drift.
 */

/** The upstream placeholder, verbatim. Case-sensitive — the corpus only ever spells it `{VAL}`. */
const VAL_PLACEHOLDER = /\{VAL\}/g;

/**
 * What the slot **reads on screen: the placeholder verbatim, braces included.** The braces do most
 * of the "this is a slot" work, so they must survive this parser — abbreviating the segment to
 * something like `"N"` here cannot be undone by any stylesheet. The complementary half is the token
 * styling in `compendium.css`, which is deliberately border-less: a hard border reads as a form
 * field or a missing value.
 */
export const VAL_SLOT_TEXT = "{VAL}";

/** The chip's class, owned by `compendium.css`. Exported so the tests pin the real class name. */
export const VAL_SLOT_CLASS = "compendium-val-slot";

/** One piece of a split string: literal text, or the placeholder standing in for a number. */
export type ValSegment = { text: string; slot: boolean };

/**
 * Split a string on `{VAL}`, into alternating literal / slot segments.
 *
 * Empty literals are dropped, which is what makes the *leading* placeholder cases work: `{VAL}/Turn`
 * splits to `[slot, "/Turn"]` rather than leading with an empty text node. A string with no
 * placeholder comes back as a single literal segment (and the empty string as no segments at all),
 * so callers can cheaply detect "nothing to do".
 */
export function splitValSlots(text: string): ValSegment[] {
  const out: ValSegment[] = [];
  let last = 0;
  // `matchAll` needs the /g flag and gives us each placeholder's index; the literal between the
  // previous match and this one is everything the corpus wants left alone.
  for (const m of text.matchAll(VAL_PLACEHOLDER)) {
    const before = text.slice(last, m.index);
    if (before) out.push({ text: before, slot: false });
    // The slot segment carries the placeholder verbatim — the split marks it up, it doesn't rewrite it.
    out.push({ text: VAL_SLOT_TEXT, slot: true });
    last = m.index + m[0].length;
  }
  const tail = text.slice(last);
  if (tail) out.push({ text: tail, slot: false });
  return out;
}

/** True when a string carries at least one placeholder — the cheap guard before doing any work. */
export function hasValSlot(text: string): boolean {
  return splitValSlots(text).some((s) => s.slot);
}

/**
 * Rewrite compendium description HTML, replacing each `{VAL}` with the slot markup.
 *
 * A string transform rather than a React one because these bodies are injected wholesale with
 * `dangerouslySetInnerHTML` (static bundled data, never user input — see `CompendiumDetail`'s
 * header note). The injected markup is a `<span>` with no `<br>` in it, so it composes with
 * `splitProseParagraphs` in either order — mark first, then split, and the paragraph boundaries
 * are exactly where they were.
 *
 * Only bare text is touched: the corpus never puts `{VAL}` inside an attribute value, so there's
 * no tag-aware parsing here.
 */
export function markValSlots(html: string): string {
  return splitValSlots(html)
    .map((seg) => (seg.slot ? `<span class="${VAL_SLOT_CLASS}">${seg.text}</span>` : seg.text))
    .join("");
}

/**
 * Render a compendium record name with its `{VAL}` placeholders as slot tokens. Emits a bare string
 * (no wrapper element) when there's nothing to mark, so the overwhelmingly common case adds no DOM —
 * and the callers' `text-overflow: ellipsis` rows keep behaving exactly as before.
 */
export function ValText({ text }: { text: string }) {
  const segments = splitValSlots(text);
  if (!segments.some((s) => s.slot)) return <>{text}</>;
  return (
    <>
      {segments.map((seg, i) =>
        seg.slot ? (
          <span key={i} className={VAL_SLOT_CLASS}>
            {seg.text}
          </span>
        ) : (
          <span key={i}>{seg.text}</span>
        ),
      )}
    </>
  );
}
